<template>
  <v-card flat class="pa-5" min-height="100%">
    <v-card flat color="transparent">
      <!-- address search section -->
      <v-card flat class="mb-10" color="transparent">
        <v-card-text>
          <AddressAutocompleteInput
            placeholder="Where would you like to go?"
            :initialValue="addressData"
            :allowFavLocations="true"
            @update="updateAddress"
          />
          <v-row no-gutters>
            <v-spacer />
            <CustomSolidButton @click="toPlanning">
              Continue
            </CustomSolidButton>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- history preview section -->
      <template v-if="unsavedTrips.length">
        <v-card-title class="pt-5 text-tertiary">
          <v-row no-gutters align="center">
            Trip history
            <v-spacer></v-spacer>
            <CustomOutlinedButton @click="showAllTrips">
              View all
            </CustomOutlinedButton>
          </v-row>
        </v-card-title>
        <v-card-text>
          <UnsavedTripCarousel />
        </v-card-text>
      </template>

      <HomeSelectedVehicleCard showBootCapacity />

      <v-card-title class="py-5 text-tertiary">
        <v-row no-gutters align="center">
          Saved trips
          <v-spacer></v-spacer>
          <CustomOutlinedButton @click="showAllTrips">
            View all
          </CustomOutlinedButton>
        </v-row>
      </v-card-title>
      <v-card-text>
        <SavedTripCard v-if="savedTrips[0]" :trip="savedTrips[0]" />
        <SavedTripCard v-if="savedTrips[1]" :trip="savedTrips[1]" />
        <SavedTripCard v-if="savedTrips[2]" :trip="savedTrips[2]" />
      </v-card-text>

      <template v-if="needsAttentionVehicles.length">
        <v-card-title class="py-5 text-tertiary"> Needs attention </v-card-title>
        <v-card-text class="pb-10">
          <DashboardVehicleListCard
            v-for="vehicle in needsAttentionVehicles"
            :key="vehicle.id"
            :vehicle="vehicle"
            class="mb-2"
            @click="navigateToVehicleDetails(vehicle)"
          />
        </v-card-text>
      </template>
    </v-card>
  </v-card>
</template>

<script setup lang="ts">
import DashboardVehicleListCard from "@/components/dashboard/vehicles/DashboardVehicleListCard.vue";
import CustomOutlinedButton from "@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import HomeSelectedVehicleCard from "@/components/vehicles/HomeSelectedVehicleCard.vue";
import AddressAutocompleteInput from "@/domains/addressLookups/AddressAutocompleteInput.vue";
import type {
  AddressAutocompleteInputUpdateObj,
  processedAddressObj,
} from "@/domains/addressLookups/types.ts";
import { useInspectionHistoryStore } from "@/domains/inspectionHistory";
import { useTripStore, type Trip } from "@/domains/trips";
import {
  getVehicleComplianceFlags,
  useVehicleStore,
  type Vehicle,
} from "@/domains/vehicles";
import type { RouteName } from "@/router";
import { useDashboardNavStore } from "@/stores/dashboardNavStore";
import { computed, ref } from "vue";
import { useRouter, type LocationQueryRaw } from "vue-router";
import SavedTripCard from "../components/trips/history/SavedTripCard.vue";
import UnsavedTripCarousel from "../components/trips/history/UnsavedTripCarousel.vue";

const router = useRouter();
const dashboardNavStore = useDashboardNavStore();

const tripStore = useTripStore();
const savedTrips = computed<Trip[]>(() => tripStore.getSavedTrips);
const unsavedTrips = computed<Trip[]>(() => tripStore.getUnsavedTrips);
const addressData = ref<processedAddressObj | undefined>(undefined);

const vehicleStore = useVehicleStore();
const inspectionHistoryStore = useInspectionHistoryStore();

/**
 * Fleet vehicles overdue or coming due for WOF/COF or registration, overdue
 * first then coming due - compliant vehicles are omitted entirely, so this
 * list is meant to be empty on a healthy fleet rather than showing every
 * vehicle colour-coded (see getVehicleComplianceFlags).
 */
const needsAttentionVehicles = computed<Vehicle[]>(() =>
  vehicleStore.vehicles
    .map((vehicle) => ({
      vehicle,
      flags: getVehicleComplianceFlags(
        vehicle,
        vehicle.externalId
          ? inspectionHistoryStore.getComplianceStatusForVehicle(
              vehicle.externalId,
            )
          : [],
      ),
    }))
    .filter(({ flags }) => flags.isOverdue || flags.isComingDue)
    .sort((a, b) => Number(b.flags.isOverdue) - Number(a.flags.isOverdue))
    .map(({ vehicle }) => vehicle),
);

function navigateToVehicleDetails(vehicle: Vehicle) {
  dashboardNavStore.showVehicleDetails(vehicle.id);
}

function showAllTrips() {
  dashboardNavStore.displayedContent = "saved-trips";
  dashboardNavStore.showDashboard = true;
}

function pushRoute(routeName: RouteName, query: LocationQueryRaw) {
  router.push({ name: routeName, query: query });
}

function toPlanning() {
  const address = addressData.value?.address;
  const lat = addressData.value?.coordinates.latitude;
  const lon = addressData.value?.coordinates.longitude;
  const name = addressData.value?.name;
  const localId = addressData.value?.localId;
  const newQuery = {
    ...router.currentRoute.value.query,
  };

  if (address) {
    newQuery.destAddress = encodeURI(address);
  }

  if (lat) {
    newQuery.destLat = lat.toString();
  }

  if (lon) {
    newQuery.destLon = lon.toString();
  }

  if (name) {
    newQuery.destName = name;
  }

  if (localId) {
    newQuery.destLocalId = localId;
  }

  pushRoute("trip-add-destination", newQuery);
}

function updateAddress(val: AddressAutocompleteInputUpdateObj) {
  addressData.value = val.addressData;
  if (val.addressData?.address && val.addressData?.address !== "") toPlanning();
}
</script>
